import React, { useEffect, useState } from 'react';
import { ResizableTable } from './ResizableTable';

type Invite = {
    inviteCode: string;
    email: string;
    sentAt: string;
};

type UserInviteManagerSectionProps = {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
};

const inviteColumns = {
    inviteCode: 180,
    email: 220,
    sentAt: 180,
};

export const UserInviteManagerSection: React.FC<UserInviteManagerSectionProps> = ({ API_BASE, authHeaders }) => {
    const [invites, setInvites] = useState<Invite[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalInvite, setModalInvite] = useState<Invite | null>(null);

    // Sorting
    const [sortField, setSortField] = useState<'sentAt' | 'email' | 'inviteCode'>('sentAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // Selection
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

    const fetchInvites = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/invite`, {
                method: 'GET',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to fetch invites.');
            }
            const data = await res.json();
            setInvites(Array.isArray(data.invites) ? data.invites : []);
        } catch (err: any) {
            setError(err.message || 'Error retrieving invites.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvites();
    }, []);

    // Mass rescind handler
    const handleMassRescind = async (selected: Invite[]) => {
        if (!selected || selected.length === 0) return;
        setLoading(true);
        setError(null);
        try {
            const rescindPayload = selected.map(invite => ({ code: invite.inviteCode }));
            const res = await fetch(`${API_BASE}/invite`, {
                method: 'DELETE',
                headers: {
                    ...authHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(rescindPayload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to rescind invites.');
            }
            await fetchInvites();
            setSelectedKeys(new Set());
        } catch (err: any) {
            setError(err.message || 'Failed to rescind invites.');
        } finally {
            setLoading(false);
        }
    };

    // Sorted data
    const sortedInvites = [...invites].sort((a, b) => {
        if (sortField === 'sentAt') {
            const aTime = new Date(a.sentAt).getTime();
            const bTime = new Date(b.sentAt).getTime();
            return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
        }
        const aVal = a[sortField] || '';
        const bVal = b[sortField] || '';
        if (aVal === bVal) return 0;
        if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
    });




    return (
        <section className="table-section">
            <h2>User Invites</h2>
            <div style={{ marginBottom: 12 }}>
                {error && <span style={{ color: 'red', marginLeft: 16 }}>{error}</span>}
                {loading && <span style={{ marginLeft: 16 }}>Processing...</span>}
            </div>
            <ResizableTable
                columns={inviteColumns}
                data={sortedInvites}
                onMassActionSubmit={() => {}}
                onRefresh={fetchInvites}
                refreshButton={true}
                sortable={true}
                sortableColumns={['inviteCode', 'email', 'sentAt']}
                deleteButton={true}
                onDelete={selected => handleMassRescind(selected as Invite[])}
                submitButton={false}
            />
        </section>
    );
};
